import {
  Alert,
  Box,
  Button,
  Center,
  FileButton,
  Group,
  Image,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCrop, IconTrash, IconUpload } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, izvedi, podatki } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica } from '../postavitev/Podatki';
import { barve } from '../tema';
import { ObrezSlike } from './ObrezSlike';
import type { Pravokotnik } from './obrez';

const NAJVEC_MB = 5;

/** Vsebina datoteke kot Base64 (brez »data:…;base64,«) za LogoPodatki.slika. */
function base64(datoteka: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(new NapakaApi('Datoteke ni bilo mogoče prebrati.', 'DATOTEKA', 0));
    r.readAsDataURL(datoteka);
  });
}

/**
 * Logotip na dokumentih: prikaz, nalaganje JPG/PNG, odstranitev in velikost na
 * dokumentih v mm (prazno = čez celo širino vsebine, 184,6 mm), obrez z miško (obreže strežnik).
 */
export function Logotip() {
  const odjemalec = useQueryClient();
  const logo = useQuery({
    queryKey: ['logo'],
    // 204 = podjetje logotipa nima
    queryFn: async ({ signal }) => (await podatki(api.GET('/api/logo', { signal }))) ?? null,
  });
  const nastavitve = useQuery({
    queryKey: ['nastavitve'],
    queryFn: ({ signal }) => podatki(api.GET('/api/nastavitve', { signal })),
  });
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);
  const [brisanje, setBrisanje] = useState(false);
  const [obrez, setObrez] = useState(false);

  const nalozi = async (datoteka: File | null) => {
    if (!datoteka) {
      return;
    }
    setNapaka(null);
    if (!/^image\/(png|jpe?g)$/.test(datoteka.type)) {
      setNapaka('Izberite sliko JPG ali PNG.');
      return;
    }
    if (datoteka.size > NAJVEC_MB * 1024 * 1024) {
      setNapaka(`Slika je prevelika (največ ${NAJVEC_MB} MB).`);
      return;
    }
    setDela('nalozi');
    try {
      await podatki(
        api.PUT('/api/logo', {
          body: { slika: await base64(datoteka), imeDatoteke: datoteka.name, vrsta: datoteka.type },
        }),
      );
      await odjemalec.invalidateQueries({ queryKey: ['logo'] });
      notifications.show({ color: 'green', message: 'Logotip je naložen.' });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  const odstrani = async () => {
    setNapaka(null);
    setDela('odstrani');
    try {
      await izvedi(api.DELETE('/api/logo'));
      await odjemalec.invalidateQueries({ queryKey: ['logo'] });
      setBrisanje(false);
      notifications.show({ color: 'green', message: 'Logotip je odstranjen.' });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  const obrezi = async (p: Pravokotnik) => {
    setNapaka(null);
    setDela('obrez');
    try {
      await podatki(api.POST('/api/logo/obrez', { body: p }));
      await odjemalec.invalidateQueries({ queryKey: ['logo'] });
      setObrez(false);
      notifications.show({ color: 'green', message: 'Logotip je obrezan.' });
    } catch (e) {
      setNapaka(e instanceof NapakaApi && e.polja.izbor ? e.polja.izbor : sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  const slika = logo.data?.slika;
  const src = `data:${logo.data?.vrsta || 'image/png'};base64,${slika}`;
  return (
    <Stack gap="md">
      <Kartica naslov="Logotip">
        {logo.isPending ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : slika && obrez ? (
          <ObrezSlike
            src={src}
            obrezi={(p) => void obrezi(p)}
            preklici={() => {
              setObrez(false);
              setNapaka(null);
            }}
            napaka={napaka}
            dela={dela === 'obrez'}
          />
        ) : slika ? (
          <Box p="md" style={{ border: `1px solid ${barve.obroba}`, borderRadius: 10, background: 'white' }}>
            <Image src={src} alt="Logotip podjetja" fit="contain" mah={220} />
          </Box>
        ) : (
          <Text size="sm" c="dimmed">
            Podjetje še nima logotipa. Dokumenti so brez njega.
          </Text>
        )}
        {logo.data?.imeDatoteke && (
          <Text size="xs" c="dimmed" mt="xs">
            {logo.data.imeDatoteke} · {Math.round((logo.data.velikost ?? 0) / 1024)} kB
          </Text>
        )}
        <Group mt="md" gap="xs" display={obrez ? 'none' : undefined}>
          <FileButton onChange={(f) => void nalozi(f)} accept="image/png,image/jpeg">
            {(p) => (
              <Button {...p} leftSection={<IconUpload size={18} />} loading={dela === 'nalozi'}>
                {slika ? 'Zamenjaj' : 'Naloži logotip'}
              </Button>
            )}
          </FileButton>
          {slika && (
            <Button variant="default" leftSection={<IconCrop size={18} />} onClick={() => setObrez(true)}>
              Obreži
            </Button>
          )}
          {slika && (
            <Button variant="light" color="red" leftSection={<IconTrash size={18} />} onClick={() => setBrisanje(true)}>
              Odstrani
            </Button>
          )}
        </Group>
        {napaka && !obrez && (
          <Alert color="red" variant="light" mt="sm" role="alert">
            {napaka}
          </Alert>
        )}
      </Kartica>
      {nastavitve.data && (
        <VelikostLogotipa
          key={`${nastavitve.dataUpdatedAt}`}
          sirina={nastavitve.data.pdfLogoSirina ?? ''}
          visina={nastavitve.data.pdfLogoVisina ?? ''}
        />
      )}
      <Modal opened={brisanje} onClose={() => setBrisanje(false)} title="Odstrani logotip" radius="lg">
        <Text size="sm">Logotip bo odstranjen z vseh dokumentov. Želite nadaljevati?</Text>
        <Group justify="flex-end" mt="md" gap="xs">
          <Button variant="subtle" onClick={() => setBrisanje(false)}>
            Prekliči
          </Button>
          <Button color="red" onClick={() => void odstrani()} loading={dela === 'odstrani'}>
            Odstrani
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}

/** Širina in višina logotipa na dokumentih v mm (nastavitvi pdfLogoSirina/pdfLogoVisina). */
function VelikostLogotipa({ sirina, visina }: { sirina: string; visina: string }) {
  const odjemalec = useQueryClient();
  const [s, setS] = useState(sirina.replace('.', ','));
  const [v, setV] = useState(visina.replace('.', ','));
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const shrani = async () => {
    setNapaka(null);
    setPolja({});
    setDela(true);
    try {
      const vse = await podatki(
        api.POST('/api/nastavitve/posodobi', {
          body: { pdfLogoSirina: s.trim().replace(',', '.'), pdfLogoVisina: v.trim().replace(',', '.') },
        }),
      );
      odjemalec.setQueryData(['nastavitve'], vse);
      notifications.show({ color: 'green', message: 'Velikost logotipa je shranjena.' });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Kartica naslov="Velikost na dokumentih">
      <Text size="sm" c="dimmed" mb="sm">
        V milimetrih. Prazno = čez celo širino vsebine (184,6 mm) z razmerjem slike; obe vrednosti = točno ta velikost.
      </Text>
      <Group grow align="flex-start">
        <TextInput
          label="Širina (mm)"
          value={s}
          onChange={(e) => setS(e.currentTarget.value)}
          inputMode="decimal"
          placeholder="184,6"
          error={polja.pdfLogoSirina}
        />
        <TextInput
          label="Višina (mm)"
          value={v}
          onChange={(e) => setV(e.currentTarget.value)}
          inputMode="decimal"
          placeholder="samodejno"
          error={polja.pdfLogoVisina}
        />
      </Group>
      {napaka && (
        <Alert color="red" variant="light" mt="sm" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end" mt="md">
        <Button onClick={() => void shrani()} loading={dela}>
          Shrani velikost
        </Button>
      </Group>
    </Kartica>
  );
}
